import { Timeline, TimelineState } from '@xzdarcy/react-timeline-editor';
import { useParams } from 'react-router-dom';
import React, { useRef, useEffect, useState } from 'react';
import {
  CustomRenderAudio,
  CustomRenderAnimation,
  CustomRenderBackground,
  CustomRenderEffect,
  CustomRenderRiggedAnimation,
  CustomRenderText,
  TimelineActionDeleteHandler,
} from './custom';
import './index.less';
import {
  CustomTimelineAction,
  CusTomTimelineRow,
  mockEffect,
  scale,
  scaleWidth,
  startLeft,
} from './mock';
import TimelinePlayer from './player';
import { AnimationCharacter, Comment, TextProps } from '../../types/typing.ts';
import { videoService } from '../../services/videoService.ts';
import { decodeName, extractTimelineActions } from './timelineUtils.ts';

interface BackgroundItem {
  id: string;
  name: string;
  imageData: any;
}
export interface Effect {
  id: string;
  type: string;
}

export interface Audio {
  id: string;
  url: string;
}

const TimelineEditor = ({
  selectedAudio,
  selectedAnimation,
  setIsCommentsOpen,
  comments,
  selectedEffect,
  selectedTexts,
  timelineState,
  onAudioRecordingComplete,
  data,
  setData,
  channel,
  setSelectedCommentId,
  selectedBackgrounds,
  setBackgroundImage,
  onDeleteTimelineAction,
  onDeleteScene,
}: {
  selectedAudio: Audio[];
  setIsCommentsOpen: any;
  comments: Comment[];
  selectedEffect: Effect[];
  selectedAnimation: AnimationCharacter[];
  selectedTexts: TextProps[];
  characterRef: any;
  effectsSpriteRef: any;
  textRefs: any;
  timelineState: any;
  onAudioRecordingComplete: (
    recordedAudioFile: File,
    duration: string
  ) => void;
  data: CusTomTimelineRow[];
  setData: (data: any) => void;
  channel: any;
  setSelectedCommentId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedBackgrounds: BackgroundItem[];
  setBackgroundImage: (imageData: any) => void;
  onDeleteTimelineAction: TimelineActionDeleteHandler;
  onDeleteScene: (sceneId: string) => void;
}) => {
  const { id: videoId } = useParams();
  const playerPanel = useRef<HTMLDivElement | null>(null);
  const autoScrollWhenPlay = useRef<boolean>(true);
  const previousSelectedAudioRef = useRef<Audio[]>([]);
  const previousSelectedEffectRef = useRef<Effect[]>([]);
  const previousSelectedAnimationRef = useRef<AnimationCharacter[]>([]);
  const previousSelectedTextRef = useRef<TextProps[]>([]);
  const previousSelectedBackgroundRef = useRef<BackgroundItem[]>([]);
  const [contextMenuAction, setContextMenuAction] = useState<{
    actionId: string;
    rowId: string;
  } | null>(null);

  const handleCommentClick = (commentId: string) => {
    setIsCommentsOpen(true); // Open the comment menu
    console.log(`Comment ${commentId} clicked`); // Debugging log
    setSelectedCommentId(commentId);

    setTimeout(() => {
      const commentElement = document.getElementById(`comment-${commentId}`);
      if (commentElement) {
        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        commentElement.classList.add('pop');

        // Remove pop effect after 1 second
        setTimeout(() => {
          commentElement.classList.remove('pop');
        }, 1000);
      }
    }, 100); // Small delay to ensure the comment is rendered
    // Remove highlight after 1 second
    setTimeout(() => {
      setSelectedCommentId(null);
    }, 1000);
  };

  useEffect(() => {
    if (selectedAudio) {
      const previousSelectedAudio = previousSelectedAudioRef.current;

      // Find existing audio that should be updated
      const matchingSelectedAudio = selectedAudio.filter((audio) =>
        previousSelectedAudio.some((prevAudio) => prevAudio.id === audio.id)
      );

      // Find removed audio (previous audio no longer present)
      const removedAudio = previousSelectedAudio.filter(
        (prevAudio) => !selectedAudio.some((audio) => audio.id === prevAudio.id)
      );

      // Update existing audio names if necessary
      matchingSelectedAudio.forEach((audio: Audio) => {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.map((row) => ({
            ...row,
            actions: row.actions.map((action) =>
              action.data.src === audio.url
                ? {
                    ...action,
                    data: { ...action.data, name: decodeName(audio.url) },
                  }
                : action
            ),
          }))
        );
      });

      // Remove deleted audio
      if (removedAudio.length > 0) {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.filter(
            (row) =>
              !removedAudio.some(
                (audio) => row.actions[0]?.data?.src === audio.url
              )
          )
        );
      }

      // Update reference
      previousSelectedAudioRef.current = selectedAudio;
    }

    return () => {};
  }, [selectedAudio]);

  useEffect(() => {
    if (selectedEffect) {
      const previousSelectedEffect = previousSelectedEffectRef.current;

      // Find the current background scene duration
      // const backgroundRow = data.find(row => row.id === "background-row");
      // let maxDuration = 10; // Default duration
      // if (backgroundRow && backgroundRow.actions.length > 0) {
      //   const currentScene = backgroundRow.actions[0]; // Get first scene
      //   maxDuration = currentScene.end - currentScene.start;
      // }

      // Update existing effects (if needed)
      selectedEffect.forEach((effect: Effect) => {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.map((row) => ({
            ...row,
            actions: row.actions.map((action) =>
              action.data.src === effect.id
                ? { ...action, data: { ...action.data, name: effect.type } }
                : action
            ),
          }))
        );
      });

      // Remove deleted effects
      const removedEffects = previousSelectedEffect.filter(
        (prevEffect) =>
          !selectedEffect.some((effect) => effect.id === prevEffect.id)
      );

      if (removedEffects.length > 0) {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.filter(
            (row) =>
              !removedEffects.some(
                (effect) => row.actions[0].data.src === effect.id
              )
          )
        );
      }

      // Update reference
      previousSelectedEffectRef.current = selectedEffect;
    }

    return () => {};
  }, [selectedEffect]);

  useEffect(() => {
    if (selectedAnimation) {
      const previousSelectedAnimation = previousSelectedAnimationRef.current;

      // Find the current background scene duration
      // const backgroundRow = data.find(row => row.id === "background-row");
      // let maxDuration = 10; // Default duration
      // if (backgroundRow && backgroundRow.actions.length > 0) {
      //   const currentScene = backgroundRow.actions[0]; // Get first scene
      //   maxDuration = currentScene.end - currentScene.start;
      // }

      // Update existing animations (if necessary)
      selectedAnimation.forEach((animation: AnimationCharacter) => {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.map((row) => ({
            ...row,
            actions: row.actions.map((action) =>
              action.data.src === animation.id
                ? { ...action, data: { ...action.data, name: animation.name } }
                : action
            ),
          }))
        );
      });

      // Find removed animations (previous ones not present in current selection)
      const removedAnimations = previousSelectedAnimation.filter(
        (prevAnimation) =>
          !selectedAnimation.some(
            (animation) => animation.id === prevAnimation.id
          )
      );

      // Remove deleted animations
      if (removedAnimations.length > 0) {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.filter(
            (row) =>
              !removedAnimations.some(
                (animation) => row.actions[0].data.src === animation.id
              )
          )
        );
      }

      // Update reference
      previousSelectedAnimationRef.current = selectedAnimation;
    }

    return () => {};
  }, [selectedAnimation]);

  useEffect(() => {
    if (selectedTexts) {
      const previousSelectedTexts = previousSelectedTextRef.current;

      // Find the current background scene duration
      // const backgroundRow = data.find(row => row.id === "background-row");
      // let maxDuration = 10; // Default duration
      // if (backgroundRow && backgroundRow.actions.length > 0) {
      //   const currentScene = backgroundRow.actions[0]; // Get first scene
      //   maxDuration = currentScene.end - currentScene.start;
      // }

      // Update existing texts
      selectedTexts.forEach((text: TextProps) => {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.map((row) => ({
            ...row,
            actions: row.actions.map((action) =>
              action.data.src === text.id
                ? { ...action, data: { ...action.data, name: text.text } }
                : action
            ),
          }))
        );
      });

      // Find removed texts (texts that existed before but are now missing)
      const removedTexts = previousSelectedTexts.filter(
        (prevText) => !selectedTexts.some((text) => text.id === prevText.id)
      );

      // Remove deleted texts
      if (removedTexts.length > 0) {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.filter(
            (row) =>
              !removedTexts.some((text) => row.actions[0].data.src === text.id)
          )
        );
      }

      previousSelectedTextRef.current = selectedTexts;
    }

    return () => {};
  }, [selectedTexts]);

  useEffect(() => {
    if (selectedBackgrounds) {
      const previousSelectedBackgrounds = previousSelectedBackgroundRef.current;

      // Find new backgrounds
      const newSelectedBackgrounds = selectedBackgrounds.filter(
        (bg) =>
          !previousSelectedBackgrounds.some((prevBg) => prevBg.id === bg.id)
      );

      // If there are no existing backgrounds, create a new row
      if (
        previousSelectedBackgrounds.length === 0 &&
        newSelectedBackgrounds.length > 0
      ) {
        const firstBackground = newSelectedBackgrounds[0];
        const newBackgroundRow: CusTomTimelineRow = {
          id: 'background-row',
          name: 'Backgrounds',
          start: 0,
          end: 10,
          actions: [
            {
              id: 'background-action-' + firstBackground.id,
              start: 0,
              end: 10,
              effectId: 'effect4',
              data: {
                src: firstBackground.id,
                name: decodeName(firstBackground.imageData),
                ref: setBackgroundImage,
                imageData: firstBackground.imageData,
              },
            },
          ],
        };
        setData((prevData: CusTomTimelineRow[]) => [
          ...prevData,
          newBackgroundRow,
        ]);
      } else {
        // Update existing background row with new backgrounds
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.map((row) => {
            if (row.id === 'background-row') {
              // Get the last action's end time
              const lastAction = row.actions[row.actions.length - 1];
              const lastEndTime = lastAction ? lastAction.end : 0;

              // Add new backgrounds sequentially
              const newActions = newSelectedBackgrounds.map(
                (background, index) => ({
                  id: 'background-action-' + background.id,
                  start: lastEndTime + index * 10, // Start where the last background ended
                  end: lastEndTime + (index + 1) * 10, // End 10 seconds after start
                  effectId: 'effect4',
                  data: {
                    src: background.id,
                    name: decodeName(background.imageData),
                    ref: setBackgroundImage,
                    imageData: background.imageData,
                  },
                })
              );

              // Update the row's end time to accommodate new backgrounds
              const newEndTime = Math.max(
                ...row.actions.map((action) => action.end),
                ...newActions.map((action) => action.end)
              );

              return {
                ...row,
                end: newEndTime,
                actions: [...row.actions, ...newActions],
              };
            }
            return row;
          })
        );
      }

      // Update existing backgrounds
      selectedBackgrounds.forEach((background) => {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.map((row) =>
            row.id === 'background-row'
              ? {
                  ...row,
                  actions: row.actions.map((action) =>
                    action.id === 'background-action-' + background.id
                      ? {
                          ...action,
                          data: {
                            ...action.data,
                            imageData: background.imageData,
                          },
                        }
                      : action
                  ),
                }
              : row
          )
        );
      });

      // Remove deleted backgrounds
      const removedBackgrounds = previousSelectedBackgrounds.filter(
        (prevBg) => !selectedBackgrounds.some((bg) => bg.id === prevBg.id)
      );

      if (removedBackgrounds.length > 0) {
        setData((prevData: CusTomTimelineRow[]) =>
          prevData.map((row) =>
            row.id === 'background-row'
              ? {
                  ...row,
                  actions: row.actions.filter(
                    (action) =>
                      !removedBackgrounds.some(
                        (bg) => action.id === 'background-action-' + bg.id
                      )
                  ),
                }
              : row
          )
        );
      }

      previousSelectedBackgroundRef.current = selectedBackgrounds;
    }
  }, [selectedBackgrounds]);

  const handleSceneBasedTimelineUpdate = (
    timelineData: CusTomTimelineRow[]
  ) => {
    // Find the background row
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    if (!backgroundRow) return timelineData;

    // Create a map of scene boundaries and calculate time shifts
    const sceneBoundaries = new Map<string, { start: number; end: number }>();
    const timeShifts = new Map<string, number>();
    let previousEndTime = 0;

    // First pass: collect boundaries and calculate shifts
    backgroundRow.actions.forEach((action) => {
      const sceneId = action.id.replace('background-action-', '');
      const currentStart = action.start;
      const currentEnd = action.end;

      // Calculate how much this scene needs to shift
      const shift = previousEndTime - currentStart;
      timeShifts.set(sceneId, shift);

      // Update the scene's boundaries
      sceneBoundaries.set(sceneId, {
        start: currentStart + shift,
        end: currentEnd + shift,
      });

      previousEndTime = currentEnd + shift;
    });

    // Update all rows to respect scene boundaries and apply shifts
    const updatedData = timelineData.map((row) => {
      if (row.id === 'background-row') {
        // Update background row with shifted times
        return {
          ...row,
          actions: row.actions.map((action) => {
            const sceneId = action.id.replace('background-action-', '');
            const shift = timeShifts.get(sceneId) || 0;
            return {
              ...action,
              start: action.start + shift,
              end: action.end + shift,
            };
          }),
        };
      }

      // Special handling for audio rows - allow them to span multiple scenes
      if (row.actions[0]?.effectId === 'effect0') {
        // Apply the appropriate shift based on the first scene the audio starts in
        let assetSceneId: string | null = null;
        for (const [sceneId, boundaries] of sceneBoundaries.entries()) {
          if (
            row.actions[0].start >= boundaries.start &&
            row.actions[0].start < boundaries.end
          ) {
            assetSceneId = sceneId;
            break;
          }
        }

        if (assetSceneId) {
          const shift = timeShifts.get(assetSceneId) || 0;
          // Apply shift to audio actions without constraining them to scene boundaries
          row.actions = row.actions.map((action) => ({
            ...action,
            start: action.start + shift,
            end: action.end + shift,
          }));
        }
      } else {
        // For non-audio elements, maintain the scene-based constraints
        let assetSceneId: string | null = null;
        for (const [sceneId, boundaries] of sceneBoundaries.entries()) {
          if (
            row.actions[0].start >= boundaries.start &&
            row.actions[0].start < boundaries.end
          ) {
            assetSceneId = sceneId;
            break;
          }
        }

        if (assetSceneId) {
          const sceneBoundary = sceneBoundaries.get(assetSceneId)!;
          const shift = timeShifts.get(assetSceneId) || 0;

          // Constrain non-audio assets to their scene's duration and apply shift
          row.actions = row.actions.map((action) => ({
            ...action,
            start: Math.max(sceneBoundary.start, action.start + shift),
            end: Math.min(sceneBoundary.end, action.end + shift),
          }));
        }
      }

      // Update the row's overall duration
      row.start = Math.min(...row.actions.map((a) => a.start));
      row.end = Math.max(...row.actions.map((a) => a.end));

      return row;
    });

    // Remove any rows that are completely empty (no actions)
    return updatedData.filter((row) => row.actions.length > 0);
  };

  const openTimelineActionMenu: TimelineActionDeleteHandler = (
    action,
    row
  ) => {
    setContextMenuAction({
      actionId: action.id,
      rowId: row.id,
    });
  };

  const deleteSceneAction: TimelineActionDeleteHandler = (action) => {
    onDeleteScene(action.id.replace('background-action-', ''));
  };

  const getContextMenuProps = (
    action: CustomTimelineAction,
    row: CusTomTimelineRow,
    onDelete: TimelineActionDeleteHandler = onDeleteTimelineAction
  ) => ({
    onDelete,
    isContextMenuOpen:
      contextMenuAction?.actionId === action.id &&
      contextMenuAction?.rowId === row.id,
    onContextMenuOpen: openTimelineActionMenu,
    onContextMenuClose: () => setContextMenuAction(null),
  });

  return (
    <div className="timeline-editor-engine">
      <div
        className="player-panel"
        id="player-ground-1"
        ref={playerPanel}
        style={{ width: '100%' }}
      ></div>
      <TimelinePlayer
        timelineState={timelineState as React.MutableRefObject<TimelineState>}
        autoScrollWhenPlay={autoScrollWhenPlay}
        data={data}
        comments={comments}
        setIsCommentOpen={setIsCommentsOpen}
        onAudioRecordingComplete={onAudioRecordingComplete}
      />
      <Timeline
        scale={scale}
        comments={comments}
        handleCommentClick={handleCommentClick}
        scaleWidth={scaleWidth}
        startLeft={startLeft}
        autoScroll={true}
        ref={(el) => {
          if (el) timelineState.current = el;
        }}
        editorData={data}
        effects={mockEffect}
        onChange={(data) => {
          setData(() => {
            // Apply scene-based constraints to the timeline data
            const updatedData = handleSceneBasedTimelineUpdate(
              data as CusTomTimelineRow[]
            );
            const actionData = extractTimelineActions(updatedData);
            // Publish only the extracted actions
            channel.publish('handleTimelineEdit', actionData);

            if (videoId) {
              videoService.patchVideo(videoId, { timeline: actionData });
            }
            return updatedData;
          });
        }}
        onContextMenuAction={(event, { action, row }) => {
          event.preventDefault();
          event.stopPropagation();

          openTimelineActionMenu(
            action as CustomTimelineAction,
            row as CusTomTimelineRow
          );
        }}
        getActionRender={(action, row) => {
          if (action.effectId === 'effect0') {
            const customAction = action as CustomTimelineAction;
            const customRow = row as CusTomTimelineRow;
            return (
              <CustomRenderAudio
                action={customAction}
                row={customRow}
                {...getContextMenuProps(customAction, customRow)}
              />
            );
          } else if (action.effectId === 'effect1') {
            const customAction = action as CustomTimelineAction;
            const customRow = row as CusTomTimelineRow;
            return (
              <CustomRenderAnimation
                action={customAction}
                row={customRow}
                {...getContextMenuProps(customAction, customRow)}
              />
            );
          } else if (action.effectId === 'effect5') {
            const customAction = action as CustomTimelineAction;
            const customRow = row as CusTomTimelineRow;
            return (
              <CustomRenderRiggedAnimation
                action={customAction}
                row={customRow}
                {...getContextMenuProps(customAction, customRow)}
              />
            );
          } else if (action.effectId === 'effect2') {
            const customAction = action as CustomTimelineAction;
            const customRow = row as CusTomTimelineRow;
            return (
              <CustomRenderEffect
                action={customAction}
                row={customRow}
                {...getContextMenuProps(customAction, customRow)}
              />
            );
          } else if (action.effectId === 'effect3') {
            const customAction = action as CustomTimelineAction;
            const customRow = row as CusTomTimelineRow;
            return (
              <CustomRenderText
                action={customAction}
                row={customRow}
                {...getContextMenuProps(customAction, customRow)}
              />
            );
          } else if (action.effectId === 'effect4') {
            const customAction = action as CustomTimelineAction;
            const customRow = row as CusTomTimelineRow;
            return (
              <CustomRenderBackground
                action={customAction}
                row={customRow}
                {...getContextMenuProps(
                  customAction,
                  customRow,
                  deleteSceneAction
                )}
              />
            );
          }
          return null;
        }}
      />
    </div>
  );
};

export default TimelineEditor;
